import React, { useState } from "react";
import { Box, Typography, Button, Grid, Card, IconButton, Container, Fade, Grow } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { CircularProgress } from "@mui/material";

const REACT_APP_BACKEND_ROUTE_URL = process.env.REACT_APP_BACKEND_ROUTE_URL;

const DataUploadByDoctor = () => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get("token");
  const patientId = urlParams.get("patient_id"); // Fallback for transition
  const facility = urlParams.get("facility");
  const orgCode = urlParams.get("org_code");
  const appMode = urlParams.get("app_mode");


  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    console.log("Files picked:", files);
    const validFiles = files.filter(file => {
      const isValid = ["image/jpeg", "image/png", "application/pdf"].includes(file.type);
      if (!isValid) toast.error(`${file.name} is not a valid file type. Use JPG, PNG or PDF.`);
      return isValid;
    });

    const filePromises = validFiles.map(file => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve({
          name: file.name,
          type: file.type,
          base64: reader.result,
          raw: file
        });
        reader.readAsDataURL(file);
      });
    });

    Promise.all(filePromises).then(newFiles => {
      setSelectedFiles(prev => [...prev, ...newFiles]);
      if (newFiles.length > 0) toast.info(`${newFiles.length} files added.`);
    });
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (!token && !patientId) {
      toast.error("Invalid QR Code: Missing Secure Session Token. Please generate a new QR code.");
      return;
    }

    if (selectedFiles.length === 0) {
      toast.warning("Please select at least one file to upload.");
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append("upload_type", "doc");
    if (patientId) {
      formData.append("patient_id", patientId);
    }
    
    selectedFiles.forEach((file) => {
      formData.append("files", file.raw);
    });

    try {
      // Use axios.create() to get a FRESH instance without global interceptors
      // This is CRITICAL because the global interceptor tries to read sessionStorage
      // and overwrites your headers with blank values on mobile devices.
      const cleanAxios = axios.create();

      console.log("Sending Upload with Token:", token ? "Yes" : "No");
      
      let uploadUrl = `${REACT_APP_BACKEND_ROUTE_URL}patient-qr-upload/?upload_type=doc`;
      if (token) {
        uploadUrl = `${REACT_APP_BACKEND_ROUTE_URL}patient-qr-upload/?token=${encodeURIComponent(token)}&upload_type=doc`;
      }

      const response = await cleanAxios.post(uploadUrl, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          "QR-Token": token || "",
        },
      });

      if (response.data?.status === "Success" || response.status === 200) {
        toast.success(response.data?.message || "Files uploaded successfully!");
        setSelectedFiles([]);
      } else {
        toast.error(response.data?.message || "Upload failed. Please try again.");
      }
    } catch (error) {
      console.error("Upload error:", error);
      const errorMessage = error.response?.data?.message || "Error uploading files. Please check your connection.";
      toast.error(errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Box sx={{
      minHeight: "100vh",
      background: "#132140", // Dark blue background matching screenshot
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      py: { xs: 2, sm: 4 },
      px: { xs: 1, sm: 2 }
    }}>
      <ToastContainer position="top-center" autoClose={3000} />
      <Container maxWidth={false} sx={{
        width: { xs: "100%", sm: "95%", md: "600px" },
      }}>
        <Grow in={true} timeout={800}>
          <Card sx={{
            p: { xs: 3, sm: 5 },
            borderRadius: 3,
            bgcolor: "#f8fafc", // Very light gray/white card
            boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
          }}>
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Typography variant="h5" gutterBottom sx={{
                fontWeight: 700,
                color: "#1e293b",
                fontSize: { xs: "1.5rem", sm: "1.85rem" }
              }}>
                Patient Document Upload
              </Typography>
              <Box sx={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "center",
                textAlign: "center",
                bgcolor: "#eef2ff",
                px: 2.5,
                py: 0.5,
                borderRadius: 5,
                border: "1px solid #d0d7f7",
                mt: 1
              }}>
                <Typography variant="caption" sx={{ color: "#1e3a8a", fontWeight: 600, fontSize: "0.8rem" }}>
                  {token ? `Secure Session Active` : `PID: ${patientId || "N/A"}`}{" "}
                  {facility ? `• ${facility}` : ""}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                p: { xs: 4, sm: 6 },
                border: "2px dashed #94a3b8",
                borderRadius: 3,
                textAlign: "center",
                cursor: "pointer",
                bgcolor: "#e2e8f0",
                transition: "all 0.2s ease",
                "&:hover": {
                  bgcolor: "#cbd5e1",
                  borderColor: "#64748b"
                },
                "&:active": { transform: "scale(0.99)" },
                mb: 3
              }}
              onClick={() => document.getElementById("mobile-file-input").click()}
            >
              <CloudUploadIcon sx={{ fontSize: { xs: 60, sm: 70 }, color: "#1e293b", mb: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#1e293b", mb: 1 }}>
                Click To Add Photos
              </Typography>
              <Typography variant="body2" sx={{ color: "#64748b" }}>
                Multiple selection supported (JPG, PNG, PDF)
              </Typography>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <label htmlFor="mobile-file-input" style={{ width: "100%", cursor: "pointer" }}>
                  <Button
                    fullWidth
                    component="span"
                    variant="outlined"
                    startIcon={<CloudUploadIcon />}
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      borderColor: "#cbd5e1",
                      bgcolor: "transparent",
                      color: "#1e293b",
                      "&:hover": { borderColor: "#94a3b8", bgcolor: "#f1f5f9" }
                    }}
                  >
                    Choose Files
                  </Button>
                </label>
              </Grid>
              <Grid item xs={12} sm={6}>
                <label htmlFor="mobile-camera-input" style={{ width: "100%", cursor: "pointer" }}>
                  <Button
                    fullWidth
                    component="span"
                    variant="outlined"
                    startIcon={<PhotoCameraIcon />}
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      borderColor: "#cbd5e1",
                      bgcolor: "transparent",
                      color: "#1e293b",
                      "&:hover": { borderColor: "#94a3b8", bgcolor: "#f1f5f9" }
                    }}
                  >
                    Take Photo
                  </Button>
                </label>
              </Grid>
            </Grid>

            {/* Hidden Inputs */}
            <input
              id="mobile-file-input"
              type="file"
              multiple
              accept="image/*,application/pdf"
              onChange={handleFileChange}
              style={{ display: "none" }}
            />
            <input
              id="mobile-camera-input"
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleFileChange}
              style={{ display: "none" }}
            />

            {selectedFiles.length > 0 && (
              <Fade in={selectedFiles.length > 0}>
                <Box sx={{ mt: 3, mb: 2 }}>
                  <Box sx={{
                    maxHeight: "180px",
                    overflowY: "auto",
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 1.5
                  }}>
                    {selectedFiles.map((file, index) => (
                      <Grow in={true} key={index} timeout={300 + index * 100}>
                        <Box sx={{
                          display: "flex",
                          alignItems: "center",
                          p: 1.5,
                          border: "1px solid #e2e8f0",
                          borderRadius: 2,
                          bgcolor: "#fff",
                        }}>
                          <InsertDriveFileIcon sx={{ color: "#64748b", mr: 1.5, fontSize: "1.5rem" }} />
                          <Typography variant="body2" sx={{
                            flexGrow: 1,
                            fontWeight: 500,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            fontSize: "0.85rem",
                            color: "#334155"
                          }}>
                            {file.name}
                          </Typography>
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); removeFile(index); }} color="error">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Grow>
                    ))}
                  </Box>
                </Box>
              </Fade>
            )}

            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleUpload}
              sx={{
                mt: selectedFiles.length > 0 ? 1 : 4,
                py: 2,
                borderRadius: 2,
                bgcolor: selectedFiles.length > 0 ? "#1e293b" : "#e2e8f0",
                color: selectedFiles.length > 0 ? "#fff" : "#94a3b8",
                "&:hover": { 
                  bgcolor: selectedFiles.length > 0 ? "#0f172a" : "#e2e8f0" 
                },
                "&.Mui-disabled": {
                  bgcolor: "#e2e8f0",
                  color: "#94a3b8",
                },
                fontSize: "1rem",
                fontWeight: 700,
                textTransform: "none",
                boxShadow: "none"
              }}
              disabled={selectedFiles.length === 0 || isUploading}
              startIcon={isUploading ? <CircularProgress size={24} color="inherit" /> : null}
            >
              {isUploading ? "Uploading..." : "Upload Documents"}
            </Button>

            <Typography variant="caption" align="center" display="block" sx={{ mt: 3, color: "#94a3b8", fontStyle: "italic", fontSize: "0.75rem" }}>
              Secure upload powered by Hlthtek™ EMR
            </Typography>
          </Card>
        </Grow>
      </Container>
    </Box>
  );
};

export default DataUploadByDoctor;
